import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// ── Inline Button ─────────────────────────────────────────────────────────────
function Button({ children, className = "", onClick, ...props }) {
  const base =
    "inline-flex items-center justify-center font-semibold transition-all cursor-pointer select-none";
  return (
    <button className={`${base} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  );
}

// ── Live feed ticker ──────────────────────────────────────────────────────────
function LiveFeed() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    const pool = [
      "📞 Call answered in Bali",
      "💬 SMS reply from Barcelona",
      "🎥 Meeting started in Tokyo",
      "🚀 New account from Lisbon",
      "📱 Connected from Chiang Mai",
      "📞 Call answered in Buenos Aires",
      "💬 SMS reply from Berlin",
      "🚀 New account from Cape Town",
    ];
    const interval = setInterval(() => {
      setEvents((current) => [
        pool[Math.floor(Math.random() * pool.length)],
        ...current.slice(0, 4),
      ]);
    }, 1500);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="fixed bottom-6 left-6 z-50 space-y-2">
      {events.map((event, index) => (
        <motion.div
          key={`${event}-${index}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-white/10 bg-black/80 px-4 py-2 text-xs text-white"
        >
          {event}
        </motion.div>
      ))}
    </div>
  );
}

// ── Fake incoming call overlay ────────────────────────────────────────────────
function FakeCall() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setShow(true);
      const timeout = setTimeout(() => setShow(false), 3500);
      return () => clearTimeout(timeout);
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  if (!show) return null;
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="fixed bottom-24 right-6 z-50 w-72 rounded-2xl border border-white/10 bg-black p-6 text-white"
    >
      <div className="text-xs text-white/60">Incoming Call on Your US Number</div>
      <div className="mt-2 text-lg font-bold">Client, New York</div>
      <div className="mt-1 text-xs text-white/30">You're in Lisbon. Doesn't matter.</div>
      <div className="mt-4 flex gap-2">
        <button className="flex-1 rounded-full bg-green-500 py-2 text-black font-semibold">
          Answer
        </button>
        <button className="flex-1 rounded-full bg-red-500 py-2 text-black font-semibold">
          Decline
        </button>
      </div>
    </motion.div>
  );
}

// ── Typing indicator ──────────────────────────────────────────────────────────
function TypingSimulation() {
  const [dots, setDots] = useState("");
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((current) => (current.length < 3 ? `${current}.` : ""));
    }, 400);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="fixed right-6 top-20 rounded-xl border border-white/10 bg-black/80 px-4 py-2 text-sm text-white z-40">
      Customer typing{dots}
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero({ navigate }) {
  return (
    <section className="px-6 py-32 text-center">
      <h1 className="text-7xl font-bold">
        One Number.
        <br />
        <span className="bg-gradient-to-r from-cyan-300 to-purple-400 bg-clip-text text-transparent">
          Any Country.
        </span>
      </h1>
      <p className="mx-auto mt-6 max-w-2xl text-xl text-white/70">
        Open your laptop. You're at work. Your number rings, your texts come in,
        your voicemail is there. Doesn't matter if you're in Bali or Barcelona.
      </p>
      <div className="mt-10 flex justify-center gap-4">
        <Button
          className="h-14 rounded-full bg-white px-10 text-lg text-black hover:bg-gray-100"
          onClick={() => navigate("/signup")}
        >
          Try It Free
        </Button>
        <Button
          className="h-14 rounded-full border border-white/20 px-10 text-white hover:bg-white/10"
          onClick={() => navigate("/signup")}
        >
          See How It Works
        </Button>
      </div>
    </section>
  );
}

// ── BYOC Section ─────────────────────────────────────────────────────────────
function BYOC() {
  return (
    <section style={{ maxWidth: 960, margin: "0 auto", padding: "80px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center" }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#a855f7", marginBottom: 14 }}>
          How It Works
        </div>
        <h2 style={{ fontSize: 32, fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: 16, letterSpacing: -0.5 }}>
          You bring the number.<br />We bring the office.
        </h2>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: 12 }}>
          ONLYnumber is built on a <strong style={{ color: "#fff" }}>Bring Your Own Carrier (BYOC)</strong> model.
          Your phone number belongs to you, not us. You connect your own Telnyx account, pick your number,
          and plug it in. We handle everything else.
        </p>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: 16 }}>
          Telnyx operates a global private network with points of presence across six continents,
          purpose-built for crystal-clear voice and messaging. The same infrastructure used by
          enterprises and Fortune 500 companies, available to you for roughly $1 to $2 per month for your number.
        </p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "8px 16px", fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 6px #10b981", display: "inline-block" }} />
          Powered by Telnyx: Global Carrier Infrastructure
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {[
          { icon: "🔒", title: "Your Number, Forever", desc: "Your number lives in your Telnyx account. Cancel ONLYnumber anytime. Your number goes with you." },
          { icon: "🌍", title: "Works Anywhere", desc: "Telnyx routes your calls through the nearest global point of presence. Full quality from Bali to Berlin." },
          { icon: "💸", title: "No Carrier Markup", desc: "You pay Telnyx directly at wholesale rates. We never mark up your call costs." },
          { icon: "⚡", title: "5-Minute Setup", desc: "Create a free Telnyx account, grab a number, paste your API key. Done." },
        ].map((card) => (
          <div key={card.title} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 22, marginBottom: 10 }}>{card.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{card.title}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", lineHeight: 1.5 }}>{card.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
function Pricing({ navigate }) {
  const plans = [
    {
      name: "Starter",
      price: "Free",
      desc: "Taste the freedom",
      highlight: false,
      badge: null,
      cta: "Start Free",
      features: [
        { text: "Your own number, yours forever", available: true },
        { text: "Inbound and Outbound Calls", available: true },
        { text: "No credit card required", available: true },
        { text: "Voicemail (missed calls disappear)", available: false },
        { text: "SMS messaging", available: false },
        { text: "Slack or Telegram messaging", available: false },
        { text: "Mobile app", available: false },
      ],
    },
    {
      name: "Pro",
      price: "$17/mo",
      desc: "Your office in your pocket",
      highlight: true,
      badge: "Most Popular",
      cta: "Get Pro",
      features: [
        { text: "Everything in Starter", available: true },
        { text: "Voicemail inbox + playback", available: true },
        { text: "Two-way SMS messaging", available: true },
        { text: "Slack or Telegram messaging", available: true },
        { text: "📱 Mobile app access", available: true, bold: true, comingSoon: true },
        { text: "Unread badges + notifications", available: true },
      ],
    },
    {
      name: "Business",
      price: "$27/mo",
      desc: "The full setup, wherever you land",
      highlight: false,
      badge: null,
      cta: "Get Business",
      features: [
        { text: "Everything in Pro", available: true },
        { text: "Instant video rooms. Ditch your Zoom subscription.", available: true },
        { text: "Scheduled meetings", available: true },
        { text: "Guest join links, no login needed", available: true },
        { text: "Real-time cross-device notifications", available: true },
        { text: "Google Calendar integration", available: true },
        { text: "Priority support", available: true },
      ],
    },
  ];

  return (
    <section className="mx-auto px-6 py-24" style={{ maxWidth: 960 }}>
      <h2 className="text-center text-4xl font-bold text-white">
        Start free. Upgrade when it gets serious.
      </h2>
      <p className="mt-3 text-center text-white/50 text-lg">
        No contracts. Cancel anytime. Your number stays yours.
      </p>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative rounded-xl border p-6 flex flex-col ${
              plan.highlight
                ? "border-purple-500 bg-purple-950/30"
                : "border-white/10 bg-white/5"
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-purple-500 px-4 py-1 text-xs font-bold text-white">
                {plan.badge}
              </div>
            )}
            <div className="text-lg font-semibold text-white">{plan.name}</div>
            <div className="mt-2 text-3xl font-bold text-white">{plan.price}</div>
            <div className="mt-1 text-sm text-white/50">{plan.desc}</div>
            <ul className="mt-6 space-y-3 text-sm flex-1">
              {plan.features.map((feature) => (
                <li
                  key={feature.text}
                  className={`flex items-start gap-2 ${
                    feature.available ? "text-white/80" : "text-white/25"
                  }`}
                  style={feature.bold && !feature.comingSoon ? { color: "#67e8f9", fontWeight: 700 } : {}}
                >
                  <span className="mt-0.5 shrink-0">
                    {feature.comingSoon ? "◷" : feature.available ? "✓" : "✗"}
                  </span>
                  <span>{feature.text}{feature.comingSoon && <span style={{ marginLeft: "6px", fontSize: "10px", fontWeight: 600, letterSpacing: "0.05em", color: "#f59e0b", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "4px", padding: "1px 5px", verticalAlign: "middle" }}>SOON</span>}</span>
                </li>
              ))}
            </ul>
            <Button
              className={`mt-8 w-full rounded-full py-2 font-semibold ${
                plan.highlight
                  ? "bg-purple-500 text-white hover:bg-purple-400"
                  : "bg-white text-black hover:bg-gray-100"
              }`}
              onClick={() => navigate("/signup")}
            >
              {plan.cta}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const items = [
    {
      q: "Do I need to get a new phone number?",
      a: "Not necessarily. If you already have a number you want to keep, you can port it into Telnyx and connect it to ONLYnumber. Or pick a fresh number from any area code you want: US, Canada, and international numbers are available through Telnyx for around $1 to $2 per month directly from them.",
    },
    {
      q: "What is Telnyx and do I have to pay them separately?",
      a: "Telnyx is the carrier infrastructure that powers your calls and texts. They are one of the world's leading cloud communications providers, the same backbone used by major enterprises. You create a free Telnyx account, buy a number (typically $1 to $2 per month), and connect it to ONLYnumber with your API key. Your ONLYnumber subscription covers the platform. Telnyx covers the actual phone network. No middleman markup, ever.",
    },
    {
      q: "Will this actually work if I'm in another country?",
      a: "Yes, that's the entire point. As long as you have a WiFi connection, your number works exactly as it would sitting in your home office. Telnyx operates a global private network with infrastructure across six continents, so call quality doesn't degrade based on where you are. Bali, Barcelona, Buenos Aires. Your clients have no idea you left.",
    },
    {
      q: "What happens to calls when I'm not logged in?",
      a: "On the free Starter plan, missed calls are missed. There's no voicemail. When you upgrade to Pro, every missed call drops into your voicemail inbox with full in-browser playback and unread notifications. It's the single biggest reason people upgrade, and for good reason.",
    },
    {
      q: "Do I need to install anything?",
      a: "Nothing to install for the browser version. Open a tab, log in, you're at work. The mobile app is available to Pro and Business subscribers and gives you full platform access from your phone. So if you step away from your laptop, you're still reachable.",
    },
    {
      q: "Can I replace Zoom with this?",
      a: "On the Business plan, yes. You get instant video rooms, scheduled meetings, and shareable guest join links with no Zoom account required for your guests. It's built directly into your phone system, so you're scheduling a call and spinning up a video room from the same tab where your number lives. Most users find they don't need Zoom after switching to Business.",
    },
  ];

  return (
    <section style={{ maxWidth: 720, margin: "0 auto", padding: "80px 24px" }}>
      <h2 style={{ textAlign: "center", fontSize: 32, fontWeight: 800, color: "#fff", marginBottom: 8, letterSpacing: -0.5 }}>
        Common questions.
      </h2>
      <p style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 15, marginBottom: 40 }}>
        Straight answers. No runaround.
      </p>
      {items.map((item) => (
        <details key={item.q} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <summary style={{
            listStyle: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "20px 0",
            cursor: "pointer",
            fontSize: 15,
            fontWeight: 600,
            color: "#fff",
            userSelect: "none",
          }}>
            {item.q}
            <span style={{ color: "#a855f7", fontSize: 22, flexShrink: 0, lineHeight: 1 }}>+</span>
          </summary>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, paddingBottom: 20 }}>
            {item.a}
          </p>
        </details>
      ))}
    </section>
  );
}

// ── Signup CTA ────────────────────────────────────────────────────────────────
function Signup({ navigate }) {
  const [email, setEmail] = useState("");
  return (
    <section className="py-24 text-center">
      <h2 className="text-4xl font-bold text-white">
        Your number is waiting.
      </h2>
      <p className="mt-3 text-white/50 text-lg">
        Set up in minutes. Works from day one.
      </p>
      <div className="mx-auto mt-8 max-w-md space-y-4">
        <input
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-black p-4 text-white placeholder-white/40 outline-none focus:border-white/30"
          autoFocus
        />
        <Button
          className="h-14 w-full rounded-full bg-white text-black hover:bg-gray-100 text-lg font-semibold"
          onClick={() => navigate("/signup")}
        >
          Claim Your Number
        </Button>
        <p className="text-xs text-white/30">No credit card. No contracts. Just your number.</p>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{
      borderTop: "1px solid rgba(255,255,255,0.07)",
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      textAlign: "center",
    }}>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.25)" }}>
        &copy; {new Date().getFullYear()} BFF Software LLC. All rights reserved.
      </div>
      <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
        <a
          href="/terms"
          style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}
          onMouseOver={(e) => (e.target.style.color = "rgba(255,255,255,0.75)")}
          onMouseOut={(e) => (e.target.style.color = "rgba(255,255,255,0.4)")}
        >
          Terms of Service
        </a>
        <span style={{ color: "rgba(255,255,255,0.15)" }}>·</span>
        <a
          href="/privacy"
          style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}
          onMouseOver={(e) => (e.target.style.color = "rgba(255,255,255,0.75)")}
          onMouseOut={(e) => (e.target.style.color = "rgba(255,255,255,0.4)")}
        >
          Privacy Policy
        </a>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Nav */}
      <nav className="flex items-center justify-between border-b border-white/10 bg-black/85 px-6 py-1.5">
        <img
          src="/logo.png"
          alt="ONLYnumber.online"
          className="h-20 w-auto object-contain"
        />
        <div className="flex items-center gap-3">
          <Button
            className="rounded-full border border-white/20 px-5 py-2 text-sm text-white hover:bg-white/10"
            onClick={() => navigate("/login")}
          >
            Log in
          </Button>
          <Button
            className="rounded-full bg-white px-5 py-2 text-sm text-black hover:bg-gray-100"
            onClick={() => navigate("/signup")}
          >
            Get Started Free
          </Button>
        </div>
      </nav>

      <LiveFeed />
      <FakeCall />
      <TypingSimulation />
      <Hero navigate={navigate} />
      <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.07)", margin: "0 32px" }} />
      <BYOC />
      <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.07)", margin: "0 32px" }} />
      <Pricing navigate={navigate} />
      <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.07)", margin: "0 32px" }} />
      <FAQ />
      <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.07)", margin: "0 32px" }} />
      <Signup navigate={navigate} />
      <Footer />
    </div>
  );
}
